import { useFetcher } from "~/hooks/useFetcher";
import {
  artifactPreviewsPath,
  sharedArtifactPreviewsPath,
  type ArtifactPreview,
} from "~/services/artifacts";

// Resolves a single artifact by name against the conversations-api previews
// endpoint. Returns the first (and only) preview, narrowing the batch
// endpoint to single-name lookup.
//
// Picks the URL based on which scope the caller is in:
//   - `conversationId` set → owner/admin/dataset-access endpoint
//   - `shareToken` set → token-scoped shares endpoint (shares-table row is
//     the authorization)
//
// Cached by SWR keyed on the request URL — repeat references to the same
// artifact reuse one in-flight request and never refetch on re-render.
export function useArtifactPreview(
  scope: { conversationId: string | null; shareToken: string | null },
  name: string
) {
  let url: string | null = null;
  if (scope.conversationId) {
    url = artifactPreviewsPath(scope.conversationId, [name]);
  } else if (scope.shareToken) {
    url = sharedArtifactPreviewsPath(scope.shareToken, [name]);
  }
  const { data, error, isLoading } = useFetcher<ArtifactPreview[]>(url);
  if (url === null) {
    return {
      preview: undefined,
      error: undefined,
      isLoading: false,
      notFound: false,
      unsupported: true,
    } as const;
  }
  const preview = data?.[0];
  // Backend returns an empty array when the name doesn't resolve — surface
  // this distinctly from a network error so the renderer can show "missing".
  const notFound = !isLoading && !error && data !== undefined && preview === undefined;
  return { preview, error, isLoading, notFound, unsupported: false } as const;
}
